import { ErrorInfo } from "react";
import { DeviceModel } from "../../models/DeviceModel";
import { DispositionCode } from "../../models/enums/DispositionCode";
import { MentionBuilderDelegate } from "../../models/builders/MentionBuilder";
import { RecallType } from "../../piping/RecallType";
import { RespondentModel } from "../../models/RespondentModel";
import { LookupTableParams } from "../../models/LookupTableParams";
import { LookupTablePage } from "../../models/LookupTablePage";
import { QuotaModel } from "../../models/QuotaModel";
import { QuestionnaireModel } from "../../models/QuestionnaireModel";
import { ValidationResultModel } from "../../models/ValidationResultModel";
import { ThemeModel } from "../../models/ThemeModel";
import { MentionModel } from "../../models/MentionModel";
import { ActionTypes } from "./types";
import { RecallMode } from "../../models/enums/RecallMode";
import { BranchToOptions } from "../../models/BranchToOptions";
import { SurveyMode } from "../../models/enums/SurveyMode";
import { NavigationResultPageModel } from "../../models/NavigationResultPageModel";
import { AnswerModel } from "../../models/AnswerModel";
import { CancellationTokenSource } from "../../models/CancellationTokenSource";

export const AUDIO_RECORDING_START = "AUDIO_RECORDING_START";
export const AUDIO_RECORDING_STOP = "AUDIO_RECORDING_STOP";
export const CHANGE_LANGUAGE = "CHANGE_LANGUAGE";
export const CACHE_RECALL = "CACHE_RECALL";
export const CONNECTION_CONNECTED = "CONNECTION_CONNECTED";
export const CONNECTION_DISCONNECTED = "CONNECTION_DISCONNECTED";
export const CONNECTION_ERROR = "CONNECTION_ERROR";
export const CONNECTION_PROGRESS = "CONNECTION_PROGRESS";
export const CONNECTION_START = "CONNECTION_START";
export const DEBUG_DISABLED = "DEBUG_DISABLED";
export const DEBUG_ENABLED = "DEBUG_ENABLED";
export const DEBUG_INFO_ENABLED = "DEBUG_INFO_ENABLED";
export const DEBUG_INFO_DISABLED = "DEBUG_INFO_DISABLED";
export const DEVICE_CHANGED = "DEVICE_CHANGED";
export const ENABLE_LEGACY_BROWSER_WARNING = "ENABLE_LEGACY_BROWSER_WARNING";
export const FILE_UPLOAD_CLEARED = "FILE_UPLOAD_CLEARED";
export const FILE_UPLOAD_COMPLETED = "FILE_UPLOAD_COMPLETED";
export const FILE_UPLOAD_FAILED = "FILE_UPLOAD_FAILED";
export const FILE_UPLOAD_PROGRESS = "FILE_UPLOAD_PROGRESS";
export const FILE_UPLOAD_STARTED = "FILE_UPLOAD_STARTED";
export const GET_FILE_FAILED = "GET_FILE_FAILED";
export const LOG_ERROR = "LOG_ERROR";
export const LOOKUP_TABLE_FETCH = "LOOKUP_TABLE_FETCH";
export const LOOKUP_TABLE_RECEIVE = "LOOKUP_TABLE_RECEIVE";
export const MOBILE_PREVIEW_DISABLED = "MOBILE_PREVIEW_DISABLED";
export const MOBILE_PREVIEW_ENABLED = "MOBILE_PREVIEW_ENABLED";
export const MOBILE_PREVIEW_TOGGLE = "MOBILE_PREVIEW_TOGGLE";
export const NAVIGATION_BRANCH_TO = "NAVIGATION_BRANCH_TO";
export const NAVIGATION_COMPLETION_QUESTION_REACHED = "NAVIGATION_COMPLETION_QUESTION_REACHED";
export const NAVIGATION_DISPLAY_ERROR = "NAVIGATION_DISPLAY_ERROR";
export const NAVIGATION_DISPLAY_MESSAGE = "NAVIGATION_DISPLAY_MESSAGE";
export const NAVIGATION_PAGE_CHANGED = "NAVIGATION_PAGE_CHANGED";
export const NAVIGATION_PAGE_READY = "NAVIGATION_PAGE_READY";
export const NAVIGATION_NEXT_PAGE = "NAVIGATION_NEXT_PAGE";
export const NAVIGATION_PREVIOUS_PAGE = "NAVIGATION_PREVIOUS_PAGE";
export const NAVIGATION_PREVIEW_QUESTION = "NAVIGATION_PREVIEW_QUESTION";
export const NAVIGATION_PUSH_HISTORY = "NAVIGATION_PUSH_HISTORY";
export const NAVIGATION_QUIT = "NAVIGATION_QUIT";
export const NAVIGATION_RESET = "NAVIGATION_RESET";
export const NAVIGATION_SHORTCUT = "NAVIGATION_SHORTCUT";
export const OPEN_UPLOADED_FILE = "OPEN_UPLOADED_FILE";
export const PREVIEW_THEME = "PREVIEW_THEME";
export const QUESTIONNAIRE_CHANGED = "QUESTIONNAIRE_CHANGED";
export const QUOTAS_CHANGED = "QUOTAS_CHANGED";
export const QUOTAS_CLOSE_STRATUM = "QUOTAS_CLOSE_STRATUM";
export const QUOTAS_CLOSE_USER_STRATUM = "QUOTAS_CLOSE_USER_STRATUM";
export const RESPONDENT_CHANGED = "RESPONDENT_CHANGED";
export const RESPONDENT_SET_APPOINTMENT = "RESPONDENT_SET_APPOINTMENT";
export const RESPONDENT_SET_EMAIL = "RESPONDENT_SET_EMAIL";
export const RESPONDENT_SET_PHONE = "RESPONDENT_SET_PHONE";
export const RESPONDENT_SET_TIMEZONE = "RESPONDENT_SET_TIMEZONE";
export const SET_DISPLAY_IN_THEME_EDITOR = "SET_DISPLAY_IN_THEME_EDITOR";
export const SET_LOOP_ROW = "SET_LOOP_ROW";
export const SET_NAVIGATION_ENABLED = "SET_NAVIGATION_ENABLED";
export const SET_PAGE_MESSAGES_ENABLED = "SET_PAGE_MESSAGES_ENABLED";
export const SET_RECALL_MODE = "SET_RECALL_MODE";
export const SET_SURVEY_MODE = "SET_SURVEY_MODE";
export const SET_VARIABLE_MENTIONS = "SET_VARIABLE_MENTIONS";
export const SET_MULTIPLE_VARIABLES_MENTIONS = "SET_MULTIPLE_VARIABLES_MENTIONS";
export const VALIDATION_CLEAR = "VALIDATION_CLEAR";
export const VALIDATION_SET_RESULT = "VALIDATION_SET_RESULT";
export const VALIDATION_SET_RESULT_NEXT = "VALIDATION_SET_RESULT_NEXT";
export const RESET_MENTIONS = "RESET_MENTIONS";
export const SERVER_PAGE_CHANGED = "SERVER_PAGE_CHANGED";
export const SET_SERVER_TRACKED_ANSWERS = "SET_SERVER_TRACKED_ANSWERS";
export const INVALIDATE_CLIENT_VARIABLES = "INVALIDATE_CLIENT_VARIABLES";
export const USE_CLIENT_VALIDATION = "USE_CLIENT_VALIDATION";
export const LOAD_ANSWERS_DTO = "LOAD_ANSWERS_DTO";
export const REFRESH_CURRENT_PAGE_QUESTIONS = "REFRESH_CURRENT_PAGE_QUESTIONS";
export const SET_SOFT_PROMPT_VISIBLE = "SET_SOFT_PROMPT_VISIBLE";
export const SET_IS_INPUT_FOCUSED = "SET_IS_INPUT_FOCUSED";
export const VALIDATE_PAGE_ON_SERVER = "VALIDATE_PAGE_ON_SERVER";
export const SET_COORDINATES = "SET_COORDINATES";
export const HIDE_ACCESSIBILITY_PANEL = "HIDE_ACCESSIBILITY_PANEL";
export const UPDATE_CARROUSEL_ACTIVE_VARIABLE = "UPDATE_CARROUSEL_ACTIVE_VARIABLE";
export const UPDATE_QUESTION_FIELD_STATUS = "UPDATE_QUESTION_FIELD_STATUS";

export const audioRecordingStart = (questionName: string, matrix: number): ActionTypes => ({
    type: AUDIO_RECORDING_START,
    questionName,
    matrix,
});
export const audioRecordingStop = (): ActionTypes => ({ type: AUDIO_RECORDING_STOP });
export const cacheRecall = (
    expression: string,
    mode: RecallType,
    result: string,
    variables: string[],
): ActionTypes => ({
    type: CACHE_RECALL,
    expression,
    mode,
    result,
    variables,
});
export const changeLanguage = (code: string): ActionTypes => ({ type: CHANGE_LANGUAGE, code });
export const connectionStart = (url: string): ActionTypes => ({ type: CONNECTION_START, url });
export const connectionConnected = (url: string): ActionTypes => ({
    type: CONNECTION_CONNECTED,
    url,
    connected: true,
});
export const connectionProgress = (progress: number): ActionTypes => ({
    type: CONNECTION_PROGRESS,
    progress,
});
export const connectionDisconnected = (reason?: string): ActionTypes => ({
    type: CONNECTION_DISCONNECTED,
    reason,
    connected: false,
});
export const connectionError = (url: string, reason?: string, error?: Error): ActionTypes => ({
    type: CONNECTION_ERROR,
    url,
    reason,
    error,
    connected: false,
});
export const debugDisabled = (): ActionTypes => ({ type: DEBUG_DISABLED });
export const debugEnabled = (): ActionTypes => ({ type: DEBUG_ENABLED });
export const debugInfoDisabled = (): ActionTypes => ({ type: DEBUG_INFO_DISABLED });
export const debugInfoEnabled = (): ActionTypes => ({ type: DEBUG_INFO_ENABLED });
export const setDisplayInThemeEditor = (displayInThemeEditor: boolean): ActionTypes => ({
    type: SET_DISPLAY_IN_THEME_EDITOR,
    displayInThemeEditor,
});
export const setSurveyMode = (mode: SurveyMode): ActionTypes => ({
    type: SET_SURVEY_MODE,
    mode,
});
export const deviceChanged = (device: DeviceModel): ActionTypes => ({
    type: DEVICE_CHANGED,
    device,
});
export const fileUploadStarted = (
    variableName: string,
    matrix: number,
    fileName: string,
    size: number,
    cancellationTokenSource: CancellationTokenSource,
): ActionTypes => ({
    type: FILE_UPLOAD_STARTED,
    variableName,
    matrix,
    fileName,
    size,
    cancellationTokenSource,
});
export const fileUploadCleared = (variableName: string, matrix: number): ActionTypes => ({
    type: FILE_UPLOAD_CLEARED,
    variableName,
    matrix,
});
export const fileUploadCompleted = (variableName: string, matrix: number): ActionTypes => ({
    type: FILE_UPLOAD_COMPLETED,
    variableName,
    matrix,
});
export const fileUploadProgress = (
    variableName: string,
    matrix: number,
    progress: number,
): ActionTypes => ({
    type: FILE_UPLOAD_PROGRESS,
    variableName,
    matrix,
    progress,
});
export const fileUploadFailed = (
    variableName: string,
    matrix: number,
    fileName: string,
    error: Error,
): ActionTypes => ({
    type: FILE_UPLOAD_FAILED,
    variableName,
    matrix,
    fileName,
    error,
});
export const getFileFailed = (
    variableName: string,
    fileName: string,
    error: Error,
): ActionTypes => ({
    type: GET_FILE_FAILED,
    variableName,
    fileName,
    error,
});
export const logError = (error: Error, info?: ErrorInfo): ActionTypes => ({
    type: LOG_ERROR,
    error,
    info,
});
export const lookupTableFetch = (
    tableId: string,
    params: LookupTableParams,
    questionName: string,
): ActionTypes => ({
    type: LOOKUP_TABLE_FETCH,
    tableId,
    params,
    questionName,
});
export const lookupTableReceive = (
    tableName: string,
    requestId: string,
    page: LookupTablePage,
    questionName: string,
): ActionTypes => ({ type: LOOKUP_TABLE_RECEIVE, tableName, requestId, page, questionName });
export const mobilePreviewEnabled = (): ActionTypes => ({ type: MOBILE_PREVIEW_ENABLED });
export const mobilePreviewDisabled = (): ActionTypes => ({ type: MOBILE_PREVIEW_DISABLED });
export const mobilePreviewToggle = (): ActionTypes => ({ type: MOBILE_PREVIEW_TOGGLE });
export const navigationCompletionQuestionReached = (): ActionTypes => ({
    type: NAVIGATION_COMPLETION_QUESTION_REACHED,
});
export const navigationBranchTo = (options: BranchToOptions): ActionTypes => ({
    type: NAVIGATION_BRANCH_TO,
    options,
});
export const navigationNextPage = (): ActionTypes => ({ type: NAVIGATION_NEXT_PAGE });
export const navigationPreviousPage = (): ActionTypes => ({ type: NAVIGATION_PREVIOUS_PAGE });
export const navigationPageReady = (): ActionTypes => ({ type: NAVIGATION_PAGE_READY });
export const navigationPreviewQuestion = (questionId: number): ActionTypes => ({
    type: NAVIGATION_PREVIEW_QUESTION,
    questionId,
});
export const navigationDisplayError = (error: Error): ActionTypes => ({
    type: NAVIGATION_DISPLAY_ERROR,
    error,
});
export const navigationDisplayMessage = (message: string): ActionTypes => ({
    type: NAVIGATION_DISPLAY_MESSAGE,
    message,
});
export const navigationQuit = (
    dispositionCode?: DispositionCode,
    options?: {
        message?: string;
        closedStrata?: boolean;
        redirectUrl?: string;
        forcedQuit?: boolean;
        discard?: boolean;
        questionId?: number;
        terminationSurveyAction?: number;
    },
): ActionTypes => ({ type: NAVIGATION_QUIT, dispositionCode, options });
export const navigationReset = (clearAnswers?: boolean): ActionTypes => ({
    type: NAVIGATION_RESET,
    clearAnswers,
});
export const navigationShortcut = (
    blockName: string,
    exitToNextQuestion: boolean,
): ActionTypes => ({
    type: NAVIGATION_SHORTCUT,
    blockName,
    exitToNextQuestion,
});
export const openUploadedFile = (variableName: string, matrix: number): ActionTypes => ({
    type: OPEN_UPLOADED_FILE,
    variableName,
    matrix,
});
export const quotasChanged = (quotas: QuotaModel[]): ActionTypes => ({
    type: QUOTAS_CHANGED,
    quotas,
});
export const quotasCloseStratum = (stratumId: number): ActionTypes => ({
    type: QUOTAS_CLOSE_STRATUM,
    stratumId,
});
export const quotasCloseUserStratum = (stratumId: number): ActionTypes => ({
    type: QUOTAS_CLOSE_USER_STRATUM,
    stratumId,
});
export const setNavigationEnabled = (enabled: boolean): ActionTypes => ({
    type: SET_NAVIGATION_ENABLED,
    enabled,
});
export const respondentChanged = (respondent: RespondentModel): ActionTypes => ({
    type: RESPONDENT_CHANGED,
    respondent,
});
export const serverPageChanged = (page: NavigationResultPageModel): ActionTypes => ({
    type: SERVER_PAGE_CHANGED,
    page,
});
export const respondentSetAppointment = (appointment: Date): ActionTypes => ({
    type: RESPONDENT_SET_APPOINTMENT,
    appointment,
});
export const respondentSetEmail = (email: string): ActionTypes => ({
    type: RESPONDENT_SET_EMAIL,
    email,
});
export const respondentSetPhone = (phone: string): ActionTypes => ({
    type: RESPONDENT_SET_PHONE,
    phone,
});
export const respondentSetTimeZone = (timeZone: string): ActionTypes => ({
    type: RESPONDENT_SET_TIMEZONE,
    timeZone,
});
export const setLoopRow = (blockName: string, row: number): ActionTypes => ({
    type: SET_LOOP_ROW,
    blockName,
    row,
});
export const setVariableMentions = (
    variableName: string,
    builder: MentionBuilderDelegate,
    matrixIndex?: number,
): ActionTypes => ({
    type: SET_VARIABLE_MENTIONS,
    variableName: variableName.toUpperCase(),
    matrixIndex,
    builder,
});
export const setMultipleVariablesMentions = (mentionsByVariable: {
    [variableName: string]: MentionBuilderDelegate;
}): ActionTypes => ({
    type: SET_MULTIPLE_VARIABLES_MENTIONS,
    mentionsByVariable,
});
export const questionnaireChanged = (questionnaire: QuestionnaireModel): ActionTypes => ({
    type: QUESTIONNAIRE_CHANGED,
    questionnaire,
});
export const previewTheme = (theme: ThemeModel): ActionTypes => ({ type: PREVIEW_THEME, theme });
export const setRecallMode = (mode: RecallMode): ActionTypes => ({ type: SET_RECALL_MODE, mode });
export const setPageMessagesEnabled = (isPageMessageEnabled: boolean): ActionTypes => ({
    type: SET_PAGE_MESSAGES_ENABLED,
    isPageMessageEnabled,
});
export const validationClear = (
    includeNewValidationResults = true,
    keepSoftPromptError = false,
    removeValidationForVariables = null,
): ActionTypes => ({
    type: VALIDATION_CLEAR,
    includeNewValidationResults,
    keepSoftPromptError,
    removeValidationForVariables,
});

export const validationSetResult = (result: ValidationResultModel): ActionTypes => ({
    type: VALIDATION_SET_RESULT,
    result,
});

export const validationSetResultNext = (result: ValidationResultModel): ActionTypes => ({
    type: VALIDATION_SET_RESULT_NEXT,
    result,
});
export const resetMentions = (mentions: {
    [variableName: string]: MentionModel[];
}): ActionTypes => ({
    type: RESET_MENTIONS,
    mentions,
});
export const setEnableLegacyBrowserWarning = (enableWarning: boolean): ActionTypes => ({
    type: ENABLE_LEGACY_BROWSER_WARNING,
    enableWarning,
});
export const setServerTrackedAnswers = (answers: AnswerModel[]): ActionTypes => ({
    type: SET_SERVER_TRACKED_ANSWERS,
    answers,
});
export const invalidateClientVariables = (): ActionTypes => ({
    type: INVALIDATE_CLIENT_VARIABLES,
});
export const useClientValidation = (useValidation: boolean): ActionTypes => ({
    type: USE_CLIENT_VALIDATION,
    useValidation,
});

export const loadAnswersDto = (answers: AnswerModel[]): ActionTypes => ({
    type: LOAD_ANSWERS_DTO,
    answers,
});

export const refreshCurrentPageQuestions = (page: NavigationResultPageModel): ActionTypes => ({
    type: REFRESH_CURRENT_PAGE_QUESTIONS,
    page,
});

export const setIsFormFocused = (isInputFocused: boolean): ActionTypes => ({
    type: SET_IS_INPUT_FOCUSED,
    isInputFocused,
});

export const setCoordinates = (latitude: number, longitude: number): ActionTypes => ({
    type: SET_COORDINATES,
    latitude,
    longitude,
});

export const setHideAccessibilityPanel = (hideAccessibilityPanel: boolean): ActionTypes => ({
    type: HIDE_ACCESSIBILITY_PANEL,
    hideAccessibilityPanel,
});

export const setCarrouselActiveVariableIndex = (
    instanceId: string,
    activeVariableIndex: number,
    isPrevBtnActive: boolean,
    isNextBtnActive: boolean,
): ActionTypes => ({
    type: UPDATE_CARROUSEL_ACTIVE_VARIABLE,
    instanceId,
    activeVariableIndex,
    isPrevBtnActive,
    isNextBtnActive,
});

export const setQuestionFieldStatus = (
    fieldIdentifiers: [string],
    isDisabled: boolean,
): ActionTypes => ({
    type: UPDATE_QUESTION_FIELD_STATUS,
    fieldIdentifiers,
    isDisabled,
});
